import { action, observable } from 'mobx'
import { required, validationContext } from 'validx'
import type { BankAccountDetails, BankAccountType } from './BankAccount'
import {
  mustBeValidAccountNumber,
  mustBeValidRoutingNumber,
  mustHaveMaxLength,
  mustHaveMinLength,
} from '@/utils/validx-util'

/**
 * View model for editing bank account details.
 */
export class BankAccountEditorViewModel {
  @observable accessor accountType: BankAccountType = 'Checking'
  @observable accessor accountNumber = ''
  @observable accessor routingNumber = ''
  @observable accessor bankName = ''

  readonly validation = validationContext(this, {
    accountType: [required('Account type is required')],
    accountNumber: [
      required('Account number is required'),
      mustBeValidAccountNumber(),
    ],
    routingNumber: [
      required('Routing number is required'),
      mustBeValidRoutingNumber(),
    ],
    bankName: [
      mustHaveMinLength(3, 'Bank name must be at least 3 characters'),
      mustHaveMaxLength(100, 'Bank name must not exceed 100 characters'),
    ],
  })

  /**
   * Resets the state of the view model to that of the given
   * bank account details, or defaults if none is specified.
   */
  @action.bound
  reset(details?: BankAccountDetails) {
    this.accountType = details?.accountType ?? 'Checking'
    this.accountNumber = details?.accountNumber ?? ''
    this.routingNumber = details?.routingNumber ?? ''
    this.bankName = details?.bankName ?? ''
    this.validation.reset()
  }

  /**
   * Maps the state of the VM to {@link BankAccountDetails}.
   */
  toBankAccountDetails(): BankAccountDetails {
    return {
      accountType: this.accountType,
      accountNumber: this.accountNumber,
      routingNumber: this.routingNumber,
      bankName: this.bankName,
    }
  }
}
